import { Grid, Radio, Typography, useEventCallback } from '@mui/material';
import { memo, useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { HeadCell } from 'shell/components/shared/table/types';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { useFormikContext } from 'formik';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import { ModalPdfViewerState } from 'shell/view-models/pdf-modal';
import { PaginationState } from 'shell/view-models/pagination-state';
import { PersonActVM } from 'shell/view-models/person-act';
import { Dossier } from 'shell/models/dossier';
import { DateTime } from 'luxon';
import { CertificationFormVM } from 'civil-status/view-models/document-birth-form';
import { PmiButton } from 'shell/components/shared/button';
import { EventLocation } from 'shell/models/person/shared';

const headCells: HeadCell<PersonActVM, keyof PersonActVM>[] = [
  {
    id: 'typeAct',
    label: 'Tipo',
  },
  {
    id: 'descriptionAct',
    label: 'Tipologia atto',
  },
  {
    id: 'actNumber',
    label: 'Numero',
  },
  {
    id: 'action',
    label: 'Azioni',
  },
];

interface ActTableProps {
  onBack: () => void;
}

const ActTableComponent = ({ onBack }: ActTableProps): React.ReactElement => {
  const [tableProps, setTableProps] = useState<PaginationState>({ page: 0, rowPerPage: 10 });

  const formContext = useFormikContext<CertificationFormVM>();

  const [pdfModal, setPdfModal] = useState<ModalPdfViewerState>({
    data: '',
    open: false,
    name: '',
  });

  const [attiStatoCivile, getAttiStatoCivile] = useHttpFetch<
    HttpResponseList<Dossier>,
    Array<PersonActVM>
  >(`${__PERSON_API_URL__}/persona/fascicolo-cittadino`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<Dossier>>) => {
      const { data } = response;

      const mappedList = data?.payload?.list?.map((item) => ({
        creationDate: item.creationDate
          ? DateTime.fromISO(item.creationDate).toFormat('dd/MM/yyyy')
          : '',
        link: item.download || '',
        element: { ...item, codiceAtto: item.codiceAtto?.toString() },
        class: item.classeDocumentale?.replace('$t!-2_', '').replace('v-1', '') || '',
        flagScaricabile: item.flagScaricabile === 'true',
        name: item.filename,
        typeAct: item.classeDocumentale,
        isAnsc: !!item.idAnsc,
        actNumber: item.idAnsc || item.numeroAtto,
        descriptionAct: item.descrizioneAtto || '',
      }));

      return mappedList.filter((item) => item.element.idAnsc || item.element.numeroAtto);
    },
  });

  const data = (attiStatoCivile.data as Array<PersonActVM>) || [];

  useEffect(() => {
    let dataRequest = {};
    if (formContext.values.accountholder?.sameAsApplicant) {
      dataRequest = {
        codiceFiscale: formContext.values.applicant?.fiscalCode
          ? { codFiscale: formContext.values.applicant?.fiscalCode }
          : null,
        cognome: formContext.values.applicant?.surname,
        nome: formContext.values.applicant?.name,
        dataNascita: formContext.values.applicant?.birthDate?.toFormat('yyyy-MM-dd'),
        idSchedaSoggettoANPR: formContext.values.applicant?.idSubjectAnpr,
        sesso: formContext.values.applicant?.gender,
        luogoNascita: {
          comune: formContext.values.applicant?.municipality?.id
            ? {
                nomeComune: formContext.values.applicant?.municipality?.description,
                siglaProvinciaIstat: formContext.values.applicant?.municipality?.province,
                codiceIstat: formContext.values.applicant?.municipality?.id,
              }
            : undefined,
          localita: formContext.values.applicant?.foreignState?.id
            ? {
                codiceStato: formContext.values.applicant?.foreignState?.id,
                descrizioneStato: formContext.values.applicant?.foreignState?.value,
              }
            : undefined,
        } as EventLocation,
      };
    } else if (formContext.values.accountholder) {
      dataRequest = {
        idAnscSoggetto: formContext.values.accountholder?.idAnsc,
        codiceFiscale: formContext.values.accountholder?.fiscalCode
          ? { codFiscale: formContext.values.accountholder?.fiscalCode }
          : null,
        cognome: formContext.values.accountholder?.surname,
        nome: formContext.values.accountholder?.name,
        dataNascita: formContext.values.accountholder?.birthDate?.toFormat('yyyy-MM-dd'),
        idSchedaSoggettoANPR: formContext.values.accountholder?.idSubjectAnpr,
        sesso: formContext.values.accountholder?.gender,
        luogoNascita: {
          comune: !formContext.values.accountholder?.isForeign
            ? {
                nomeComune: formContext.values.accountholder?.municipality?.description,
                siglaProvinciaIstat: formContext.values.accountholder?.municipality?.province,
                codiceIstat: formContext.values.accountholder?.municipality?.id,
              }
            : undefined,
          localita: formContext.values.accountholder?.isForeign
            ? {
                codiceStato: formContext.values.accountholder?.foreignState?.id,
                descrizioneStato: formContext.values.accountholder?.foreignState?.value,
              }
            : undefined,
        } as EventLocation,
      };
    }

    if (
      Object.keys(dataRequest).length > 0 &&
      !attiStatoCivile.isLoading &&
      attiStatoCivile.pristine
    ) {
      getAttiStatoCivile({
        data: dataRequest,
      });
    }
  }, [formContext.values.applicant, formContext.values.accountholder, attiStatoCivile]);

  const handleChangePage = useEventCallback((page: number) => {
    setTableProps({ page, rowPerPage: tableProps.rowPerPage });
  });

  const handleChangeRowsPerPage = useEventCallback((rowPerPage: number) => {
    setTableProps({ page: 0, rowPerPage });
  });

  const onVisibilityPress = useCallback(
    (cell) => {
      formContext.setFieldValue('chosenDocument', cell);
    },
    [formContext.setFieldValue]
  );

  const list: Array<PersonActVM> = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        actNumber: item.isAnsc
          ? `${item.actNumber}`
          : // ${item.element?.comune || ''} ${item.element?.provincia || ''} ${
            //     item.formationDate || ''
            //   }`
            item.actNumber,
        action: (
          <>
            <span>
              <Radio
                checked={formContext.values?.chosenDocument?.actNumber === item.actNumber}
                onChange={() => onVisibilityPress(item)}
                value={item.actNumber}
                name={`radio-${item.actNumber}`}
                inputProps={{ 'aria-label': `Radio ${item.actNumber}` }}
              />
            </span>
          </>
        ),
      })) || []
    );
  }, [data, onVisibilityPress, formContext.values?.chosenDocument?.actNumber]);

  const nameAccountholder: string = useMemo(() => {
    if (
      !Boolean(formContext.values.accountholder?.name || formContext.values.accountholder?.surname)
    ) {
      return '';
    }

    return `${formContext.values.accountholder?.surname || ''} ${
      formContext.values.accountholder?.name || ''
    }`;
  }, [formContext.values.accountholder]);

  const nameApplicant: string = useMemo(() => {
    if (!Boolean(formContext.values.applicant?.name || formContext.values.applicant?.surname)) {
      return '';
    }

    return `${formContext.values.applicant?.surname || ''} ${
      formContext.values.applicant?.name || ''
    }`;
  }, [formContext.values.applicant]);

  return (
    <Grid item xs={12} container spacing={1}>
      <Grid item xs={12}>
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Lista Atti di Stato Civile
          {nameAccountholder || nameApplicant ? (
            <Fragment>
              &nbsp;di&nbsp;
              <span style={{ fontWeight: 'bold' }}>{nameAccountholder || nameApplicant}</span>
            </Fragment>
          ) : undefined}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {!attiStatoCivile.data ? undefined : list.length > 0 ? (
          <Table
            onSort={noop}
            head={headCells}
            orders={{}}
            page={tableProps.page}
            rowsPerPage={tableProps.rowPerPage}
            body={list}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista atti stato civile"
            isNotLayoutFixed
            noSort
            hideButtons
            count={list.length}
          />
        ) : (
          <Typography color="secondary.main">Nessun elemento da visualizzare.</Typography>
        )}
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data || ''}
          name={pdfModal.name}
          title="Visualizza documento"
          isFullScreen
          onClose={() => {
            setPdfModal({ data: '', open: false, name: '' });
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
          <div className="pmi-w-25 pmi-w-lg-15">
            <PmiButton
              variant="outlined"
              fullWidth
              color="secondary"
              onClick={() => {
                formContext.setFieldValue('chosenDocument', null);
                onBack();
              }}
            >
              Indietro
            </PmiButton>
          </div>
          <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
            <PmiButton
              variant="outlined"
              color="cdmBrandThird"
              fullWidth
              type="submit"
              disabled={!formContext.values.chosenDocument}
            >
              Avanti
            </PmiButton>
          </div>
        </div>
      </Grid>
    </Grid>
  );
};

ActTableComponent.displayName = 'Act Table component';

const ActTable = memo(ActTableComponent);

export default ActTable;
